import React, { useState, useEffect, useRef } from "react";
import { toastSuccess, toastError, toastInfo } from "../utils/toast";
import { productService, companyService } from "../services/api";
import {
  Table,
  EditIcon,
  DeleteIcon,
  FormPopup,
  DeletePopUp,
  UploadPreviewPopUp,
} from "../components/common";
import { parseFileToPreview } from "../utils/uploadPreviewParser";
import { downloadProductUploadTemplate } from "../utils/downloadProductUploadTemplate";

const PRODUCT_COLUMNS = [
  { key: "sku", label: "SKU" },
  { key: "skuName", label: "SKU Name" },
  { key: "description", label: "Description" },
  // { key: "supplierName", label: "Supplier" },
  // {
  //   key: "skuCategory",
  //   label: "SKU Type",
  //   align: "center",
  //   render: (val) => (val ? String(val).replace("_", " ") : "—"),
  // },
  // {
  //   key: "manufacturingDate",
  //   label: "MFG Date",
  //   align: "center",
  //   render: (val) => (val ? new Date(val).toLocaleDateString() : "—"),
  // },
  // {
  //   key: "expiryDate",
  //   label: "EXP Date",
  //   align: "center",
  //   render: (val) => (val ? new Date(val).toLocaleDateString() : "—"),
  // },

  {
    key: "companies",
    label: "Company",
    render: (_, row) => {
      const list = row.companies || [];
      if (!list.length) return "—";
      return list.map((c) => `${c.name} (${c.companyCode || '—'})`).join(", ");
    },
  },
  { key: "uom", label: "UOM", align: "center" },
  {
    key: "casesPerPallet",
    label: "Cases/Pallet",
    align: "center",
    render: (val) => (val != null && val !== "" ? val : "—"),
  },
  { key: "weight", label: "Weight (kg)", align: "center" },
  {
    key: "dimensions",
    label: "Dimensions (L×B×H cm)",
    render: (_, row) =>
      row.dimensions
        ? `${row.dimensions.length} × ${row.dimensions.breadth} × ${row.dimensions.height}`
        : "—",
  },
  {
    key: "isBatchControlled",
    label: "Batch Controlled",
    align: "center",
    render: (val) => (val ? "Yes" : "No"),
  },
];

export function ProductMaster() {
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);

  const [formData, setFormData] = useState({
    sku: "",
    skuName: "",
    description: "",
    uom: "PCS",
    basePrice: 0,
    casesPerPallet: "",
    innerUom: "",
    innerQtyPerOuter: "",
    weight: 0,
    dimensions: { length: 0, breadth: 0, height: 0 },
    isBatchControlled: true,
    companies: [],
    supplierName: "",
    skuCategory: "NON_CORE", // default
    manufacturingDate: "",
    expiryDate: "",
    location: "",
  });

  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [uploadPreviewOpen, setUploadPreviewOpen] = useState(false);
  const [uploadPreviewFileName, setUploadPreviewFileName] = useState("");
  const [uploadPreviewHeaders, setUploadPreviewHeaders] = useState([]);
  const [uploadPreviewRows, setUploadPreviewRows] = useState([]);
  const [uploadSelectedFile, setUploadSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = useRef(null);
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const result = await companyService.getCompanies({ isActive: "true" });
      setCompanies(result.data || []);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => fetchProducts(), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
      const result = await productService.getProducts(params);
      setProducts(result.data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      sku: "",
      skuName: "",
      description: "",
      uom: "PCS",
      basePrice: 0,
      casesPerPallet: "",
      innerUom: "",
      innerQtyPerOuter: "",
      weight: 0,
      dimensions: { length: 0, breadth: 0, height: 0 },
      isBatchControlled: true,
      companies: [],

      // ✅ NEW defaults
      supplierName: "",
      skuCategory: "NON_CORE",
      manufacturingDate: "",
      expiryDate: "",
      location: "",
    });
    setEditingProductId(null);
    setFormErrors({});
    setTouched({});
  };

  const handleSubmit = async (e) => {
   if (e && e.preventDefault) e.preventDefault();
 
   const dataToSubmit = e && e.preventDefault ? formData : e;
 
   // ✅ 1) basePrice normalize + validate (ADD HERE)
   const basePriceNum =
     dataToSubmit?.basePrice === "" || dataToSubmit?.basePrice == null
       ? 0
       : Number(dataToSubmit.basePrice);
 
   if (Number.isNaN(basePriceNum) || basePriceNum < 0) {
     toastError("Base Price must be a non-negative number");
     return;
   }
 
   // ✅ 2) inner config optional mapping + validate (ADD HERE)
   const outerUom = String(dataToSubmit?.uom || "").trim().toUpperCase();
   const innerUom = String(dataToSubmit?.innerUom || "").trim().toUpperCase();
   
   let innerUomConfig = undefined;
   
   if (innerUom) {
     if (innerUom === outerUom) {
       toastError("Inner UOM cannot be same as Outer UOM");
       return;
     }
     innerUomConfig = { uom: innerUom }; // ✅ qty removed
   }
   // ✅ 3) NOW build payload (your existing payload, but add basePrice + innerUomConfig)
   const payload = {
     ...dataToSubmit,
     sku: dataToSubmit?.sku?.trim() ? dataToSubmit.sku.trim() : undefined,
     skuName: dataToSubmit?.skuName?.trim(),
     description: dataToSubmit?.description?.trim(),
 
     supplierName: dataToSubmit?.supplierName?.trim(),
     skuCategory: dataToSubmit?.skuCategory,
 
     manufacturingDate: dataToSubmit?.manufacturingDate ? dataToSubmit.manufacturingDate : undefined,
     expiryDate: dataToSubmit?.expiryDate ? dataToSubmit.expiryDate : undefined,
     location: dataToSubmit?.location?.trim() ? dataToSubmit.location.trim() : undefined,
 
     // ✅ NEW fields to backend
     basePrice: basePriceNum,
     innerUomConfig, // undefined if user didn't fill inner
   };
 
   // (optional cleanup - if you don’t want UI-only fields to go)
  delete payload.innerUom;
  delete payload.innerQtyPerOuter;

  // casesPerPallet: include only if value is present; otherwise omit
  const cppRaw = dataToSubmit?.casesPerPallet;
  if (cppRaw !== "" && cppRaw != null && cppRaw !== 0) {
    payload.casesPerPallet = Number(cppRaw);
  }

  setFormErrors((prev) => ({ ...prev, sku: undefined }));

  try {
    if (editingProductId) {
       await productService.updateProduct(editingProductId, payload);
       toastSuccess("Product updated successfully");
       resetForm();
       setShowForm(false);
       fetchProducts();
     } else {
       const res = await productService.createProduct(payload);
       if (res?.error) {
         const err = new Error(res.message || res.error || "Failed to create product");
         err.response = { data: res };
         throw err;
       }
       toastSuccess("Product created successfully");
       resetForm();
       setShowForm(false);
       fetchProducts();
     }
   } catch (error) {
     console.error("Error saving product:", error);
     const data = error?.response?.data || error?.data || {};
     let msg = data.message || data.error || "Failed to create product";
     if (data.error === "Duplicate key error" && data.details?.sku) {
       msg = `${data.details.sku} already exists. Please enter another SKU code.`;
       setFormErrors((prev) => ({ ...prev, sku: msg }));
       setTouched((prev) => ({ ...prev, sku: true }));
       setTimeout(() => document.querySelector('input[name="sku"]')?.focus(), 0);
     }
     toastError(msg);
   }
 };
  const productFields = [
    // ROW 1 (5 fields)
    {
      name: "sku",
      label: "SKU Code",
      type: "text",
      placeholder: "Leave blank to auto-generate",
      required: false,
      grid: "1",
      disabled: !!editingProductId,
    },
    { name: "skuName", label: "SKU Name", type: "text", required: true, grid: "1" },
    {
      name: "description",
      label: "Product Description",
      type: "text",
      required: true,
      grid: "1",
    },
    {
      name: "companies",
      label: "Company",
      type: "multiselect",
      placeholder: "-- Select Company --",
      options: companies.map((c) => ({
        label: `${c.companyCode} - ${c.name}`,
        value: c._id || c.id,
      })),
      grid: "1",
    },
    {
      name: "basePrice",
      label: "Base Price",
      type: "number",
      required: false,
      min: 0,
      step: 0.01,
      grid: "1",
    },
    // ROW 2 (5 fields)
    {
      name: "uom",
      label: "Unit of Measure(Outer)",
      type: "select",
      required: true,
      options: [
        { label: "Pieces (PCS)", value: "PCS" },
        { label: "Kilogram (KG)", value: "KG" },
        { label: "Liter (L)", value: "L" },
        { label: "Box", value: "BOX" },
        { label: "Pack", value: "PACK" },
        { label: "Case", value: "CASE" },
        { label: "Unit", value: "UNIT" },
      ],
      grid: "1",
    },
    {
      name: "innerUom",
      label: "Unit of Measure(Inner)",
      type: "select",
      required: false,
      options: [
        { label: "Pieces (PCS)", value: "PCS" },
        { label: "Kilogram (KG)", value: "KG" },
        { label: "Liter (L)", value: "L" },
        { label: "Box", value: "BOX" },
        { label: "Pack", value: "PACK" },
        { label: "Case", value: "CASE" },
        { label: "Unit", value: "UNIT" },
      ],
      grid: "1",
    },
    {
      name: "weight",
      label: "Gross weight (Kg)",
      type: "number",
      required: true,
      min: 0,
      step: 0.01,
      grid: "1",
    },
    { name: "supplierName", label: "Supplier Name", type: "text", required: false, grid: "1" },
    {
      name: "skuCategory",
      label: "SKU Category",
      type: "select",
      required: false,
      options: [
        { label: "Core", value: "CORE" },
        { label: "Non Core", value: "NON_CORE" },
      ],
      grid: "1",
    },
    // ROW 3 (3 fields only; cols 4-5 empty)
    {
      name: "manufacturingDate",
      label: "Manufacturing Date",
      type: "date",
      required: false,
      grid: "1",
    },
    {
      name: "expiryDate",
      label: "Expiry Date",
      type: "date",
      required: false,
      grid: "1",
    },
    { name: "location", label: "Location", type: "text", required: false, grid: "1" },
    {
      name: "casesPerPallet",
      label: "Cases per pallet",
      type: "number",
      required: false,
      min: 1,
      placeholder: "e.g., 40",
      grid: "1",
    },
    // Dimensions section card (full width)
    {
      type: "custom",
      grid: "full",
      render: () => (
        <div
          style={{
            border: "1px solid #E4E7EC",
            borderRadius: 14,
            backgroundColor: "#fff",
            padding: 18,
            marginTop: 16,
          }}
        >
          <h3
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: "#101828",
              marginBottom: 16,
            }}
          >
            Dimensions (cm)
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 26,
            }}
          >
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "#344054" }}>
                Length
              </label>
              <input
                type="number"
                min={0}
                step={0.1}
                value={formData.dimensions?.length ?? ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    dimensions: {
                      ...prev.dimensions,
                      length: parseFloat(e.target.value) || 0,
                    },
                  }))
                }
                className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                style={{ height: 44, padding: "0 14px" }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "#344054" }}>
                Breath
              </label>
              <input
                type="number"
                min={0}
                step={0.1}
                value={formData.dimensions?.breadth ?? ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    dimensions: {
                      ...prev.dimensions,
                      breadth: parseFloat(e.target.value) || 0,
                    },
                  }))
                }
                className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                style={{ height: 44, padding: "0 14px" }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "#344054" }}>
                Height
              </label>
              <input
                type="number"
                min={0}
                step={0.1}
                value={formData.dimensions?.height ?? ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    dimensions: {
                      ...prev.dimensions,
                      height: parseFloat(e.target.value) || 0,
                    },
                  }))
                }
                className="w-full rounded-[10px] border border-[#D0D5DD] bg-white text-sm placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                style={{ height: 44, padding: "0 14px" }}
              />
            </div>
          </div>
        </div>
      ),
    },
    // Batch Controlled Product (full width, left aligned)
    { name: "isBatchControlled", label: "Batch Controlled Product", type: "yesno", grid: "full" },
  ];

  const handleEdit = (row) => {
    setFormData({
      sku: row.sku || "",
      skuName: row.skuName || "",
      description: row.description || "",
      uom: row.uom || "PCS",
      weight: row.weight || 0,
      dimensions: {
        length: row.dimensions?.length || 0,
        breadth: row.dimensions?.breadth || 0,
        height: row.dimensions?.height || 0,
      },
      isBatchControlled: typeof row.isBatchControlled === "boolean" ? row.isBatchControlled : false,
      companies: (row.companies || []).map((c) => c._id || c.id),
      basePrice: typeof row.basePrice === "number" ? row.basePrice : 0,
      casesPerPallet: row.casesPerPallet != null && row.casesPerPallet !== "" ? row.casesPerPallet : "",

      innerUom: row.innerUomConfig?.uom || "",
      innerQtyPerOuter:
        row.innerUomConfig?.quantityPerOuter != null
          ? String(row.innerUomConfig.quantityPerOuter)
          : "",
      supplierName: row.supplierName || "",
      skuCategory: row.skuCategory || "NON_CORE",
      manufacturingDate: row.manufacturingDate ? String(row.manufacturingDate).slice(0, 10) : "",
      expiryDate: row.expiryDate ? String(row.expiryDate).slice(0, 10) : "",
      location: row.location || "",
    });

    setEditingProductId(row._id || row.id);
    setShowForm(true);
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeletePopUpOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) return;
    const id = rowToDelete._id || rowToDelete.id;
    try {
      setDeleteLoading(true);
      const result = await productService.deleteProduct(id);
      setDeletePopUpOpen(false);
      setRowToDelete(null);
      await fetchProducts();
      const message = result?.message || (result?.modified ? "Product deactivated successfully" : "Product deactivated");
      toastSuccess(message);
    } catch (err) {
      console.error("Error deleting product:", err);
      toastError(err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const productActions = [
    { label: "Edit", icon: EditIcon, onClick: handleEdit },
    { label: "Delete", icon: DeleteIcon, onClick: handleDelete },
  ];

  const resetUploadPreview = () => {
    setUploadPreviewOpen(false);
    setUploadPreviewFileName("");
    setUploadPreviewHeaders([]);
    setUploadPreviewRows([]);
    setUploadSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUploadProductClick = () => fileInputRef.current?.click();

  const handleUploadFileSelect = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    try {
      const { headers, rows, fileName } = await parseFileToPreview(file);
      setUploadPreviewFileName(fileName);
      setUploadPreviewHeaders(headers);
      setUploadPreviewRows(rows);
      setUploadSelectedFile(file);
      setUploadPreviewOpen(true);
    } catch (err) {
      toastError(err);
    }
  };

  const handleDownloadFormat = () => {
    downloadProductUploadTemplate();
    toastSuccess("Format downloaded. Fill and upload to add products.");
  };

  const handleUploadApply = async () => {
    if (!uploadSelectedFile) {
      resetUploadPreview();
      return;
    }
    setUploadLoading(true);
    try {
      const res = await productService.bulkUploadProductExcel(uploadSelectedFile);
      const created = res?.data?.created ?? 0;
      const errors = res?.data?.errors || [];
      const errCount = errors.length;

      if (created > 0 && errCount === 0) {
        toastSuccess(`✅ Success! ${created} product(s) uploaded successfully.`, { autoClose: 4000 });
      } else if (created > 0 && errCount > 0) {
        toastSuccess(`✅ ${created} product(s) uploaded successfully.`, { autoClose: 3000 });
        setTimeout(() => {
          const errorMessages = errors.slice(0, 5).map((e) => e.error || (typeof e === "string" ? e : "Invalid data"));
          const errorText =
            errCount <= 5 ? errorMessages.join(". ") : `${errorMessages.join(". ")}. ...and ${errCount - 5} more error(s).`;
          toastError(`⚠️ ${errCount} row(s) had errors: ${errorText}`, { autoClose: 6000 });
        }, 500);
      } else if (errCount > 0) {
        const errorMessages = errors.slice(0, 5).map((e) => e.error || (typeof e === "string" ? e : "Invalid data"));
        const errorText =
          errCount <= 5 ? errorMessages.join(". ") : `${errorMessages.join(". ")}. ...and ${errCount - 5} more error(s).`;
        toastError(`❌ Upload failed. ${errCount} error(s) found: ${errorText}`, { autoClose: 7000 });
      } else {
        toastInfo("ℹ️ No products found in file. Please check your file and try again.", { autoClose: 4000 });
      }

      fetchProducts();
      setTimeout(() => resetUploadPreview(), 500);
    } catch (err) {
      toastError(err, { autoClose: 5000 });
      setTimeout(() => resetUploadPreview(), 500);
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="mb-6 w-full">
        <div
          className="flex items-center justify-between w-full flex-wrap gap-4"
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #EEF1F5",
            borderRadius: 18,
            padding: "16px 18px",
          }}
        >
          <h1 style={{ fontSize: 20, fontWeight: 600, color: "#101828", margin: 0 }}>
            Product Master
          </h1>

          <div className="flex items-center gap-[14px] flex-wrap">
            <div className="relative" style={{ width: 320 }}>
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by ID"
                className="w-full rounded-[12px] border border-[#D0D5DD] bg-white pl-10 pr-4 text-sm text-gray-800 placeholder:text-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                style={{ height: 44 }}
              />
            </div>

            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="shrink-0 rounded-[12px] bg-[#188742] text-white font-semibold transition hover:bg-[#15733a] active:scale-[0.98]"
              style={{ height: 44, paddingLeft: 18, paddingRight: 18 }}
            >
              Create New Product
            </button>

            <div className="relative shrink-0" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                className="flex items-center justify-center rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] transition hover:bg-[rgba(24,135,66,0.06)]"
                style={{ width: 44, height: 44 }}
                aria-label="More options"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
                  <circle cx="12" cy="6" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="18" r="1.5" />
                </svg>
              </button>

              {menuOpen && (
                <div
                  className="absolute right-0 top-full z-50 mt-1 rounded-[12px] border border-[#EEF1F5] bg-white shadow-lg overflow-hidden"
                  style={{ width: 220, padding: 8 }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      handleDownloadFormat();
                      setMenuOpen(false);
                    }}
                    className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#F6F7FA]"
                    style={{ height: 40 }}
                  >
                    Download Format
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleUploadProductClick();
                      setMenuOpen(false);
                    }}
                    className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#F6F7FA]"
                    style={{ height: 40 }}
                  >
                    Upload Product
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <input type="file" ref={fileInputRef} accept=".xlsx,.xls,.csv" className="hidden" onChange={handleUploadFileSelect} />

      <UploadPreviewPopUp
        open={uploadPreviewOpen}
        onClose={resetUploadPreview}
        title="Upload Product - File overview"
        fileName={uploadPreviewFileName}
        previewHeaders={uploadPreviewHeaders}
        previewRows={uploadPreviewRows}
        onApply={handleUploadApply}
        onCancel={resetUploadPreview}
        loading={uploadLoading}
      />

      <DeletePopUp
        open={deletePopUpOpen}
        onClose={() => {
          setDeletePopUpOpen(false);
          setRowToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        entityLabel="product"
      />

      <FormPopup
        open={showForm}
        title={editingProductId ? "Edit Product" : "Create New Product"}
        onClose={() => {
          setShowForm(false);
          resetForm();
        }}
        onSubmit={handleSubmit}
        submitText={editingProductId ? "Update" : "Save"}
        values={formData}
        setValues={setFormData}
        fields={productFields}
        errors={formErrors}
        touched={touched}
        width="xl"
      />

      <Table columns={PRODUCT_COLUMNS} data={products} loading={loading} emptyMessage="No products found" actions={productActions} />
    </div>
  );
}
